import { roundById, sidesMove, type RoundKind, type SideResult } from '../rounds';
import type { BacktestRound } from './backtest';

/**
 * Compact wire format for a day of rounds (~16 KB instead of ~240 KB of JSON objects):
 * ids as deltas (almost always 1), closing digits and moves as one character per round.
 * endAt is not sent: it follows from the round id on the shared clock.
 */
export interface PackedRounds {
  kind: RoundKind;
  /** First id, then the difference to the previous one. */
  ids: number[];
  /** One char per round: 0–9. */
  digits: string;
  /**
   * One char per round: U(p), D(own), F(lat), -(no reading); a–i = a round read per side (BacktestRound.paid):
   * what Sobe and Desce paid, its move following from them.
   */
  moves: string;
}

const MOVE_CHAR = { UP: 'U', DOWN: 'D', FLAT: 'F' } as const;
const CHAR_MOVE: Record<string, BacktestRound['move']> = { U: 'UP', D: 'DOWN', F: 'FLAT', '-': null };
const PAID: readonly SideResult[] = ['WIN', 'LOSS', 'REFUND'];
const paidChar = (p: NonNullable<BacktestRound['paid']>) => String.fromCharCode(97 + PAID.indexOf(p.rise) * 3 + PAID.indexOf(p.fall));

export function packRounds(kind: RoundKind, rounds: readonly BacktestRound[]): PackedRounds {
  const ids: number[] = [];
  let digits = '';
  let moves = '';
  let prev = 0;
  for (const r of rounds) {
    ids.push(ids.length ? r.id - prev : r.id);
    prev = r.id;
    digits += String(r.digit);
    moves += r.paid ? paidChar(r.paid) : r.move ? MOVE_CHAR[r.move] : '-';
  }
  return { kind, ids, digits, moves };
}

export function unpackRounds(p: PackedRounds): BacktestRound[] {
  const out: BacktestRound[] = new Array(p.ids.length);
  let id = 0;
  for (let i = 0; i < p.ids.length; i++) {
    id = i ? id + p.ids[i]! : p.ids[0]!;
    const base = { id, endAt: roundById(p.kind, id).endAt, digit: p.digits.charCodeAt(i) - 48 };
    const code = p.moves.charCodeAt(i) - 97;
    if (code >= 0 && code < 9) {
      const paid = { rise: PAID[Math.floor(code / 3)]!, fall: PAID[code % 3]! };
      out[i] = { ...base, move: sidesMove(paid.rise, paid.fall), paid };
    } else out[i] = { ...base, move: CHAR_MOVE[p.moves[i]!] ?? null };
  }
  return out;
}
